import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { AppShell } from "@/components/AppShell";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Search } from "lucide-react";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">{children}</Label>
);
const n = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const COLS = [
  ["sn", "S.N."], ["date", "Date"], ["bale_no", "Bale No."], ["bill_no", "Purc. Bill No."],
  ["company", "Company"], ["quality", "Quality"], ["item", "Item"], ["shade", "Shade"],
  ["qty", "Qty"], ["than", "Than"], ["rate", "Rate"], ["total", "Total"],
  ["bill_disc_pct", "Bill Disc %"], ["bill_disc_amount", "Bill Disc Amt"], ["after_bill_disc", "After Bill Disc"],
  ["other_disc_pct", "Other Disc %"], ["other_disc_amount", "Other Disc Amt"], ["after_other_disc", "After Other Disc"],
  ["expense_pct", "Bill Exp %"], ["expense_amount", "Bill Exp Amt"], ["after_expense", "After Bill Exp"],
  ["igst_pct", "IGST %"], ["igst_amount", "IGST Amt"], ["after_igst", "After IGST"],
  ["round_off", "Round Off"], ["net_total", "Net Total"], ["voucher_no", "Voucher No."],
];
const TEXT_COLS = new Set(["sn", "date", "bale_no", "bill_no", "company", "quality", "item", "shade", "voucher_no"]);

export default function PurchaseRegister() {
  const [profile, setProfile] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [supplier, setSupplier] = useState("");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = () => ({ supplier, search, date_from: from, date_to: to });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/purchase-register", { params: params() });
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const download = async (format) => {
    try {
      const res = await api.get("/purchase-register/export", {
        params: { ...params(), format },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `purchase-register.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/masters/supplier").then(({ data }) => setSuppliers(data.map((m) => m.name))).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Display</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">Purchase Register</h1>
          <p className="mt-2 text-sm text-zinc-500">
            Line wise purchase detail — bale, supplier bill no., every discount, expense, IGST and round-off step.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 py-6 sm:grid-cols-2 lg:grid-cols-5 lg:items-end">
          <div>
            <L>Supplier</L>
            <select
              data-testid="register-supplier-filter"
              className="mt-2 h-10 w-full rounded-sm border border-zinc-300 bg-white px-2 text-sm"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
            >
              <option value="">All suppliers</option>
              {suppliers.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <L>Search bale / bill / item</L>
            <div className="relative mt-2">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                data-testid="register-search-input"
                className="rounded-sm pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="e.g. B-12"
              />
            </div>
          </div>
          <div>
            <L>From</L>
            <DateInput data-testid="register-from-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={from} onChange={(v) => setFrom(v)} />
          </div>
          <div>
            <L>To</L>
            <DateInput data-testid="register-to-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={to} onChange={(v) => setTo(v)} />
          </div>
          <div className="flex gap-2">
            <Button data-testid="register-run-button" className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90" onClick={load}>
              Show
            </Button>
            <Button data-testid="register-export-pdf" variant="outline" className="rounded-sm" onClick={() => download("pdf")}>
              <Download size={14} className="mr-1.5" /> PDF
            </Button>
            <Button data-testid="register-export-excel" variant="outline" className="rounded-sm" onClick={() => download("excel")}>
              <FileSpreadsheet size={14} className="mr-1.5" /> Excel
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="py-16 text-center text-sm text-zinc-500">Loading…</p>
        ) : !data || data.rows.length === 0 ? (
          <div data-testid="register-empty-state" className="border border-dashed border-zinc-300 p-12 text-center">
            <h3 className="font-head text-xl font-bold tracking-tight">Nothing found</h3>
            <p className="mt-1 text-sm text-zinc-500">Record a purchase voucher or widen the date range.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-6" data-testid="register-totals">
              {[
                ["Lines", data.totals.lines],
                ["Qty", data.totals.qty],
                ["Than", data.totals.than],
                ["Total", `₹${n(data.totals.total)}`],
                ["IGST", `₹${n(data.totals.igst_amount)}`],
                ["Net total", `₹${n(data.totals.net_total)}`],
              ].map(([label, value]) => (
                <div key={label} className="border border-zinc-200 bg-white p-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">{label}</p>
                  <p className="font-mono-num mt-1.5 text-lg font-semibold">{value}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 overflow-x-auto border border-zinc-200 bg-white" data-testid="register-table">
              <table className="w-full text-xs">
                <thead className="border-b border-zinc-200 bg-zinc-50">
                  <tr className="text-[9px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                    {COLS.map(([key, label]) => (
                      <th key={key} className={`whitespace-nowrap px-2 py-2 ${TEXT_COLS.has(key) ? "text-left" : "text-right"}`}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {data.rows.map((r, i) => (
                    <tr key={`${r.voucher_no}-${i}`} data-testid={`register-row-${i}`} className="hover:bg-zinc-50">
                      {COLS.map(([key]) => (
                        <td
                          key={key}
                          className={`whitespace-nowrap px-2 py-1.5 ${
                            TEXT_COLS.has(key) ? "" : "font-mono-num text-right"
                          } ${key === "net_total" ? "font-semibold" : ""}`}
                        >
                          {TEXT_COLS.has(key) ? r[key] || "-" : n(r[key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </AppShell>
  );
}
